import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  LinearProgress, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

import { STATUS, statusText } from '../utils/format';

export const GELB = '#F5C400';

// Eigener Dateiname statt logo.svg: unter /logo.svg lieferte das alte Portal die
// Startseite aus, und Browser haben diese falsche Antwort noch im Zwischenspeicher.
export const LOGO_URL = `${process.env.PUBLIC_URL}/profiware-logo.svg`;

export function Logo({ hoehe = 40, sx }) {
  return (
    <Box
      component="img" src={LOGO_URL} alt="ProfiWare"
      // feiner Rand: auf dunklem Grund verschwindet die schwarze Logo-Kachel sonst
      sx={{ height: hoehe, width: hoehe, display: 'block', borderRadius: `${Math.round(hoehe * 0.16)}px`, boxShadow: '0 0 0 1px rgba(245,196,0,0.35)', ...sx }}
    />
  );
}

// Kopf der oeffentlichen Seiten (Anmeldung, Aktivierung, Verlaengerung)
export function Markenkopf({ titel, text }) {
  return (
    <Box sx={{ textAlign: 'center', mb: 3 }}>
      <Logo hoehe={84} sx={{ mx: 'auto', mb: 2 }} />
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.12em', lineHeight: 1.4, display: 'block' }}>
        ProfiWare
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{titel}</Typography>
      {text && <Typography variant="body2" color="text.secondary">{text}</Typography>}
    </Box>
  );
}

export const HINTERGRUND_OEFFENTLICH = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
  background: 'radial-gradient(ellipse at top left, rgba(245,196,0,0.1) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(245,196,0,0.06) 0%, transparent 60%)',
};

export function Seitenkopf({ titel, untertitel, children }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{titel}</Typography>
        {untertitel && <Typography variant="body2" color="text.secondary">{untertitel}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{children}</Box>
    </Box>
  );
}

export function StatusPille({ status, tage, size = 'small' }) {
  if (!status) return <Typography variant="body2" color="text.secondary">–</Typography>;
  return <Chip size={size} label={statusText(status, tage)} color={STATUS[status]?.farbe || 'default'} />;
}

export function Kopieren({ text, titel = 'Kopieren' }) {
  const [kopiert, setKopiert] = useState(false);
  const kopieren = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text || '');
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1800);
    } catch (_) { /* Zwischenablage gesperrt - der Text steht daneben zum Markieren */ }
  };
  return (
    <Tooltip title={kopiert ? 'Kopiert' : titel}>
      <IconButton size="small" onClick={kopieren} aria-label={titel}>
        {kopiert ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

export function Mono({ children, sx }) {
  return (
    <Box component="span" sx={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '0.85em', wordBreak: 'break-all', ...sx }}>
      {children}
    </Box>
  );
}

// "4 von 5 belegt" mit Balken. belegt == null: die Installation meldet es (noch) nicht.
export function Auslastung({ belegt, max, wort = 'belegt' }) {
  if (belegt == null) {
    return <Typography variant="body2">{max} <Typography component="span" variant="body2" color="text.secondary">erlaubt</Typography></Typography>;
  }
  const voll = max > 0 && belegt >= max;
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="body2" sx={{ color: voll ? 'error.main' : 'text.primary' }}>{belegt} von {max} {wort}</Typography>
      <LinearProgress
        variant="determinate" value={max > 0 ? Math.min(100, (belegt / max) * 100) : 0}
        color={voll ? 'error' : 'primary'} sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
      />
    </Box>
  );
}

const AMPEL = { gruen: '#10B981', gelb: '#F59E0B', rot: '#EF4444' };
export function Ampel({ farbe, children }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
      <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: AMPEL[farbe] || AMPEL.gruen, flexShrink: 0 }} />
      {children}
    </Box>
  );
}

// Ersetzt window.confirm: sagt, was passiert, und beschriftet den Knopf mit der Aktion
export function Bestaetigung({ offen, titel, text, knopf = 'Löschen', farbe = 'error', onJa, onNein }) {
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const ja = async () => {
    setLaedt(true);
    setFehler('');
    try {
      await onJa();
    } catch (e) {
      setFehler(e?.response?.data?.detail || 'Das hat nicht geklappt.');
    } finally {
      setLaedt(false);
    }
  };
  const zu = () => { setFehler(''); onNein(); };
  return (
    <Dialog open={!!offen} onClose={laedt ? undefined : zu} maxWidth="xs" fullWidth>
      <DialogTitle>{titel}</DialogTitle>
      <DialogContent>
        {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}
        <Typography variant="body2" color="text.secondary">{text}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={zu} disabled={laedt}>Abbrechen</Button>
        <Button variant="contained" color={farbe} onClick={ja} disabled={laedt}>{knopf}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Beschriftete Zeile "Begriff: Wert" fuer die Akte
export function Paar({ begriff, children }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary">{begriff}</Typography>
      <Box sx={{ minWidth: 0, fontSize: '0.875rem' }}>{children}</Box>
    </>
  );
}

export const paarRaster = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '9.5rem minmax(0, 1fr)' }, columnGap: 2, rowGap: 1, alignItems: 'center' };
